import React from "react";
import NotInformation from "../../../../../components/NotInformation";
import DeleteModal from "../../../../Modals/DeleteModal";
import CustomFieldsTableApp from "../Functionality/CustomFieldsTableApp";
import DividerWithText from "../../../../../components/DividerWithText";
import {CustomTextField} from "../../../../../components/CustomTextField";
import {CustomSelect} from "../../../../../components/CustomSelect";
import {CustomSwitch} from "../../../../../components/CustomSwitch";
import ButtonAction from "../../../../../components/buttons/ButtonAction";
import {AddIcon, TrashOutlineIconWhite} from "../../../../../components/buttons/IconButtons";
import CustomTable from "../../../../../components/table/CustomTable";
import CustomCard from "../../../../../components/CustomCard";
import EditCustomFieldModal from "../Modal/EditCustomFieldModal";
import {FIELD_TYPE, FIELD_TYPES_MODEL} from "../Model/Model";

/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer
 * @component CustomFieldsTable
 * @description React component that renders the UI for managing custom fields.
 * It includes form inputs, table display, modals for edit and delete, and action buttons.
 *
 * @param {boolean} emptyFields - Flag indicating if empty field validation is active.
 * @param {Array} fields - List of custom fields.
 * @param {function} setFields - Setter function to update the list of custom fields.
 * @param {Object} fieldData - The current custom field data being edited/added.
 *
 * @returns {JSX.Element} Rendered CustomFieldsTable component.
 */

const CustomFieldsTable = (props) => {
    const {emptyFields, fields, setFields, fieldData, disabled = false} = props;
    const {
        t,
        selectedList,
        setSelectedList,
        deleteModal,
        setDeleteModal,
        tableId,
        searchId,
        isAllChecked,
        setIsAllChecked,
        thead,
        tbody,
        editModal,
        setEditModal,
        editField,
        setEditField,
        getActionsButtons,
        pagination,
        deleteFn,
        handleCheckboxChange,
        handleDeleteAll,
        handleAddField,
        handleChange
    } = CustomFieldsTableApp(props);


    return (
        <>
            <div className={'col-12 my-4'}>
                <DividerWithText title={t('custom_fields')} />
            </div>

            {/* The form only builds new custom fields, so it is hidden in the read only view */}
            {!disabled && (
            <div className="row align-items-center">
                <div className="col-12 col-md pl-md-0">
                    <CustomTextField
                        id="txt_cft_field_name"
                        name="fieldName"
                        value={fieldData.fieldName}
                        trans="field_name"
                        type="text"
                        onChange={handleChange}
                        required={fields?.length === 0}
                        emptyFields={emptyFields && fields?.length === 0}
                    />
                </div>

                <div className="col-12 col-md">
                    <CustomSelect
                        id="slt_cft_field_type"
                        name="fieldType"
                        value={fieldData.fieldType || ''}
                        keyName="id"
                        onChange={handleChange}
                        list={FIELD_TYPES_MODEL()}
                        trans="field_type"
                        required={fields?.length === 0}
                        emptyFields={emptyFields && fields?.length === 0}
                        showNoSelectionOption={false}
                    />
                </div>

                {fieldData.fieldType !== FIELD_TYPE.CHECKBOX.name &&
                    <div className="col-12 col-md-auto input-padding-bottom">
                        <CustomSwitch
                            id="cb_cft_required"
                            checked={fieldData.isRequired}
                            onChange={(e) =>
                                handleChange({
                                    target: { name: e.target.name, value: !fieldData.isRequired },
                                })
                            }
                            trans={t('required')}
                            name="isRequired"
                            className={"w-180px"}
                        />
                    </div>
                }


                <div className="col-12 col-md-auto input-padding-bottom pr-md-0">
                    <ButtonAction
                        id="btn_cft_add_visitor_field"
                        name="add"
                        Icon={AddIcon}
                        onClick={handleAddField}
                        className="btn-secondary-600"
                        disabled={!fieldData.isValid()}
                    />
                </div>
            </div>
            )}


            <div className={"col-12"}>
                {fields?.length > 0 ?    <CustomCard>
                        {!disabled && (
                        <div className='col-12 px-0 pb-2'>
                            <div className='d-flex justify-content-end'>
                                <ButtonAction
                                    id='btn_cft_del_visitor_field'
                                    name={'delete'}
                                    Icon={TrashOutlineIconWhite}
                                    className='btn-icon-delete'
                                    onClick={() => {
                                        setDeleteModal(true);
                                    }}
                                    disabled={selectedList?.length === 0}
                                />
                            </div>
                        </div>
                        )}

                        {/* Rendering the CustomTable component with the necessary props */}
                        <CustomTable
                            tableId={tableId} // Table ID
                            searchId={searchId} // ID for the search input field
                            checkbox={!disabled} // Selection is only needed to delete fields
                            checkboxTitle={"de_/_select_all"} // Title for the "Select All / Deselect All" checkbox
                            isAllChecked={isAllChecked} // Whether all checkboxes are selected
                            onChangeCheckbox={handleDeleteAll} // Function to handle checkbox state changes
                            thead={thead} // The table header
                            tbody={tbody} // The table body data
                            columnActions={!disabled} // Enable actions for each column
                            getActionsButtons={disabled ? undefined : getActionsButtons} // Function to generate action buttons for each row
                            handleCheckboxChange={handleCheckboxChange} // Handler for checkbox change on individual rows
                            selectedList={selectedList} // List of selected rows
                            withoutHeight={true}
                        />
                        {/* Rendering pagination control */}
                        { pagination() } {/* Function to render pagination */}
                    </CustomCard>
                    :
                    <NotInformation
                        card={true}
                        text={typeof fields === "undefined" ? t('loading') : t('no_custom_fields')}
                        showButton={false}
                    />
                }
            </div>


            <DeleteModal
                show={deleteModal}
                onHide={() => setDeleteModal(false)}
                data={selectedList}
                onClickFn={() => {
                    deleteFn(selectedList);
                    setDeleteModal(false);
                }}
                title_modal={'delete_custom_fields'}
                element_modal={'custom_fields'}
                isReset={() => setSelectedList([])}
                isAllReset={setIsAllChecked}
            />

            <EditCustomFieldModal
                show={editModal}
                onHide={() => {
                    setEditModal(false);
                }}
                setData={setEditField}
                data={editField}
                list={fields}
                setList={setFields}
            />
        </>
    );
};

export default CustomFieldsTable;
